import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { GEN_LABELS } from "../i18n";
import { Loader2, Search, Users, Download, BarChart3 } from "lucide-react";

const GENS = ["genx", "geny", "genz", "genalpha"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { t, lang } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [genFilter, setGenFilter] = useState("");

  useEffect(() => {
    api.get("/admin/results").then(({ data }) => setData(data));
  }, []);

  const types = useMemo(() => {
    if (!data) return [];
    return Object.keys(data.distribution || {}).sort();
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    return data.results.filter((r) => {
      if (typeFilter && r.mbti_type !== typeFilter) return false;
      if (genFilter && r.generation !== genFilter) return false;
      if (!term) return true;
      return (
        (r.name || "").toLowerCase().includes(term) ||
        (r.email || "").toLowerCase().includes(term) ||
        (r.mbti_type || "").toLowerCase().includes(term)
      );
    });
  }, [data, q, typeFilter, genFilter]);

  const fmtDate = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString(lang === "id" ? "id-ID" : "en-US", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const exportCsv = () => {
    const rows = [[t("th_name"), t("th_email"), t("th_type"), t("th_generation"), t("th_session"), t("th_date")]];
    filtered.forEach((r) => {
      rows.push([
        r.name || "",
        r.email || "",
        r.mbti_type || "",
        GEN_LABELS[r.generation] || r.generation || "",
        r.session_title || t("direct_test"),
        fmtDate(r.created_at),
      ]);
    });
    const csv = rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "amt-participants.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-heading text-4xl" style={{ color: "var(--text)" }}>{t("admin_title")}</h1>
            <p className="text-sm mt-1" style={{ color: "var(--text-dim)" }}>{t("admin_sub")}</p>
          </div>
          <button data-testid="export-csv-btn" onClick={exportCsv} className="btn-outline-gold inline-flex items-center gap-2 px-4 py-2.5 text-sm">
            <Download size={16} /> {t("export_csv")}
          </button>
        </div>

        {data === null ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin gold-text" size={30} /></div>
        ) : (
          <>
            {/* stats */}
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <div className="card-amt p-5 flex items-center gap-4" data-testid="stat-total">
                <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ background: "rgba(201,165,87,0.12)" }}>
                  <Users className="gold-text" size={20} />
                </div>
                <div>
                  <div className="font-heading text-2xl" style={{ color: "var(--text)" }}>{data.total}</div>
                  <div className="text-xs" style={{ color: "var(--text-dim)" }}>{t("total_participants")}</div>
                </div>
              </div>
              <div className="card-amt p-5 sm:col-span-2">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 size={16} className="gold-text" />
                  <span className="text-xs uppercase tracking-wider" style={{ color: "var(--gold)" }}>{t("distribution")}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {types.length === 0 ? (
                    <span className="text-sm" style={{ color: "var(--text-dim)" }}>—</span>
                  ) : (
                    types.map((ty) => (
                      <span key={ty} className="px-2.5 py-1 rounded-sm text-xs font-mono" style={{ background: "rgba(201,165,87,0.12)", color: "var(--gold)" }}>
                        {ty} · {data.distribution[ty]}
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* filters */}
            <div className="flex flex-wrap gap-3 mb-5">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-dim)" }} />
                <input
                  data-testid="admin-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("search_placeholder")}
                  className="input-amt w-full pl-9 pr-4 py-2.5 text-sm"
                />
              </div>
              <select data-testid="filter-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="input-amt px-3 py-2.5 text-sm">
                <option value="">{t("all_types")}</option>
                {types.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
              </select>
              <select data-testid="filter-gen" value={genFilter} onChange={(e) => setGenFilter(e.target.value)} className="input-amt px-3 py-2.5 text-sm">
                <option value="">{t("all_gens")}</option>
                {GENS.map((g) => <option key={g} value={g}>{GEN_LABELS[g]}</option>)}
              </select>
            </div>

            {/* table */}
            <div className="card-amt overflow-x-auto" data-testid="admin-table">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    {[t("th_name"), t("th_email"), t("th_type"), t("th_generation"), t("th_session"), t("th_date")].map((h) => (
                      <th key={h} className="text-left font-semibold px-4 py-3 whitespace-nowrap" style={{ color: "var(--gold)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-10 text-center" style={{ color: "var(--text-dim)" }} data-testid="admin-empty">{t("no_results_found")}</td></tr>
                  ) : (
                    filtered.map((r, i) => (
                      <tr
                        key={r.result_id}
                        data-testid={`admin-row-${i}`}
                        onClick={() => navigate(`/result/${r.result_id}`)}
                        className="cursor-pointer transition-colors"
                        style={{ borderBottom: "1px solid var(--border)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(201,165,87,0.06)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <td className="px-4 py-3 whitespace-nowrap" style={{ color: "var(--text)" }}>{r.name || "-"}</td>
                        <td className="px-4 py-3 whitespace-nowrap" style={{ color: "var(--text-dim)" }}>{r.email || "-"}</td>
                        <td className="px-4 py-3"><span className="font-mono font-semibold gold-text">{r.mbti_type}</span></td>
                        <td className="px-4 py-3 whitespace-nowrap" style={{ color: "var(--text-dim)" }}>{GEN_LABELS[r.generation] || r.generation}</td>
                        <td className="px-4 py-3 whitespace-nowrap" style={{ color: "var(--text-dim)" }}>{r.session_title || t("direct_test")}</td>
                        <td className="px-4 py-3 whitespace-nowrap" style={{ color: "var(--text-dim)" }}>{fmtDate(r.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
